import { Link } from "@tanstack/react-router";
import type { MouseEventHandler, ReactNode } from "react";
import { cn } from "../lib/utils";
import type { PrerequisOuvert } from "./contract";
import { nomCourt } from "./format";

/**
 * Les deux liens du portefeuille : vers le detail d'un pari, et vers le guide d'un geste.
 *
 * Ils vivent dans leur propre fichier parce que trois pages s'en servent (l'accueil, son
 * rail « ce qui t'attend » et la page d'un guide) : les laisser dans l'accueil forcerait le
 * rail a importer la page qui l'importe.
 */

/**
 * Le texte d'un geste, qui mene a son guide. Sans guide (un vieux prerequis fait, ou un
 * geste qu'aucun fichier ne documente), la ligne reste du texte : mieux vaut pas de lien
 * qu'un lien qui tombe sur un 404.
 *
 * `children` remplace le libelle la ou c'est le pari qui distingue deux liens plutot que le
 * geste : l'encart des gestes partages porte trois fois le meme `quoi`, un par dossier.
 */
export function LienGuide({
  geste,
  className,
  children,
  nomAccessible,
}: {
  geste: PrerequisOuvert;
  className?: string;
  children?: ReactNode;
  /**
   * Le nom lu par un lecteur d'ecran, quand le texte visible ne suffit pas a distinguer
   * deux liens de la page. Il contient toujours le texte visible (WCAG 2.5.3).
   */
  nomAccessible?: string;
}) {
  const classe = cn("min-w-0 font-medium underline underline-offset-2", className);
  const libelle = children ?? geste.quoi;
  const texte = <span className={cn("min-w-0", className)}>{libelle}</span>;
  if (geste.cible === null || geste.guide === null) return texte;
  if (geste.source === "hors-idee") {
    return (
      <Link
        aria-label={nomAccessible}
        className={classe}
        params={{ nom: geste.guide }}
        to="/portefeuille/gestes/$nom"
      >
        {libelle}
      </Link>
    );
  }
  // La cible d'un prerequis de fiche s'ecrit `<slug>/<guide>` : c'est la meme paire que
  // celle de l'adresse, et la seule qui fasse foi (le pari porte un prefixe de source).
  // Une cible sans barre, ou qui commence par une barre, n'a pas de slug : pas de lien.
  const coupure = geste.cible.indexOf("/");
  if (coupure <= 0) return texte;
  return (
    <Link
      aria-label={nomAccessible}
      className={classe}
      params={{ nom: geste.guide, slug: geste.cible.slice(0, coupure) }}
      to="/portefeuille/idees/$slug/gestes/$nom"
    >
      {libelle}
    </Link>
  );
}

/**
 * Le lien vers le detail, deduit de l'identifiant du pari (`idee:<slug>`, `scout:<id>`).
 *
 * `court` n'affiche que le nom court (le titre entier reste au survol) ; `discret` ne
 * souligne qu'au survol et au focus, la ou la place et la graisse disent deja que c'est un
 * lien. Ailleurs le lien vit au milieu d'un texte et garde son soulignement.
 */
export function LienPari({
  id,
  titre,
  court = false,
  discret = false,
  className,
  onClick,
}: {
  id: string;
  titre: string;
  court?: boolean;
  discret?: boolean;
  className?: string;
  /** Pose la ou le lien vit dans un `summary`, pour que le clic ne deplie pas le groupe. */
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}) {
  const separateur = id.indexOf(":");
  const source = id.slice(0, separateur);
  const reste = id.slice(separateur + 1);
  const texte = court ? nomCourt(titre) : titre;
  // Des que le nom court est demande, le titre entier va au survol : meme quand rien n'a
  // ete coupe, le lien peut etre tronque par sa colonne (`truncate`).
  const infobulle = court ? titre : undefined;
  const classe = cn(
    "font-medium underline-offset-2",
    discret ? "hover:underline focus-visible:underline" : "underline",
    className,
  );

  if (source === "idee") {
    return (
      <Link
        className={classe}
        onClick={onClick}
        params={{ slug: reste }}
        title={infobulle}
        to="/portefeuille/idees/$slug"
      >
        {texte}
      </Link>
    );
  }
  if (source === "scout") {
    return (
      <Link
        className={classe}
        onClick={onClick}
        params={{ id: reste }}
        title={infobulle}
        to="/portefeuille/scout/$id"
      >
        {texte}
      </Link>
    );
  }
  // Un module a venir peut poser une source que cette page ne connait pas : elle
  // affiche le titre sans lien plutot que de fabriquer une adresse fausse.
  return (
    <span className={cn("font-medium", className)} title={infobulle}>
      {texte}
    </span>
  );
}
